import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Filter, Download, ChevronLeft, ChevronRight,
  CheckCircle, Clock, AlertCircle, Calendar, Mail, Phone, Building,
  Layers, Trophy, Brain, Code
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamRegistryAdmin = () => {
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Filters
  const [program, setProgram] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Expanded team
  const [expandedTeam, setExpandedTeam] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, [program, paymentStatus, search, pagination.page]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 25
      });
      if (program) params.append('program', program);
      if (paymentStatus) params.append('payment_status', paymentStatus);
      if (search) params.append('search', search);

      const res = await axios.get(`${API}/admin/unified-teams?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTeams(res.data.teams || []);
      setStats(res.data.stats || {});
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Failed to fetch teams:', err);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPagination(p => ({ ...p, page: 1 }));
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (program) params.append('program', program);
      if (paymentStatus) params.append('payment_status', paymentStatus);

      const res = await axios.get(`${API}/admin/unified-teams/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert to CSV
      const data = res.data.export_data;
      if (!data || data.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teams_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} teams`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed');
    }
  };

  const getProgramIcon = (prog) => {
    switch (prog) {
      case 'hackathon_v1': return <Trophy className="text-amber-500" size={18} />;
      case 'hackathon_v2': return <Trophy className="text-blue-500" size={18} />;
      case 'iapl': return <Brain className="text-purple-500" size={18} />;
      case 'codearena': return <Code className="text-indigo-500" size={18} />;
      default: return <Users size={18} />;
    }
  };

  const getProgramColor = (prog) => {
    switch (prog) {
      case 'hackathon_v1': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'hackathon_v2': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'iapl': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'codearena': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPaymentBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle size={12} /> Paid
        </span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
          <Clock size={12} /> Pending
        </span>;
      case 'active':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          <CheckCircle size={12} /> Active
        </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
          <AlertCircle size={12} /> {status}
        </span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Team Registry</h1>
        <p className="text-gray-600">Unified view of all registered teams across programs</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Trophy className="text-amber-600" size={20} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.by_program?.hackathon_v1 || 0}</div>
                <div className="text-xs text-gray-500">Hackathon 1st Ed</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Trophy className="text-blue-600" size={20} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.by_program?.hackathon_v2 || 0}</div>
                <div className="text-xs text-gray-500">Hackathon 2nd Ed</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Brain className="text-purple-600" size={20} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.by_program?.iapl || 0}</div>
                <div className="text-xs text-gray-500">AI League</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Code className="text-indigo-600" size={20} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.by_program?.codearena || 0}</div>
                <div className="text-xs text-gray-500">Code Arena</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by team name or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </form>

          {/* Program Filter */}
          <select
            value={program}
            onChange={(e) => { setProgram(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Programs</option>
            <option value="hackathon_v1">Hackathon 1st Edition</option>
            <option value="hackathon_v2">Hackathon 2nd Edition</option>
            <option value="iapl">AI League (IAPL)</option>
            <option value="codearena">Code Arena</option>
          </select>

          {/* Payment Filter */}
          <select
            value={paymentStatus}
            onChange={(e) => { setPaymentStatus(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Status</option>
            <option value="completed">Paid</option>
            <option value="pending">Pending</option>
          </select>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Teams Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Team</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Program</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Leader</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Members</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-4 py-4"><div className="h-6 bg-gray-200 rounded w-24"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-36"></div></td>
                    <td className="px-4 py-4 text-center"><div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div></td>
                    <td className="px-4 py-4 text-center"><div className="h-6 bg-gray-200 rounded w-16 mx-auto"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                  </tr>
                ))
              ) : teams.length > 0 ? (
                teams.map((team) => (
                  <React.Fragment key={`${team.program}-${team.id}`}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
                    >
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">{team.team_name}</div>
                        {team.domain && (
                          <div className="text-xs text-gray-500 mt-1">{team.domain}</div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getProgramColor(team.program)}`}>
                          {getProgramIcon(team.program)}
                          {team.program_label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">{team.leader_name}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          <a href={`mailto:${team.leader_email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                            <Mail size={12} /> {team.leader_email}
                          </a>
                          {team.leader_phone && (
                            <a href={`tel:${team.leader_phone}`} className="text-sm text-gray-600 flex items-center gap-1">
                              <Phone size={12} /> {team.leader_phone}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="font-medium text-gray-900">{team.total_members}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {getPaymentBadge(team.payment_status)}
                        {team.payment_status === 'completed' && team.amount_paid > 0 && (
                          <div className="text-xs text-gray-500 mt-1">₹{team.amount_paid?.toLocaleString()}</div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Calendar size={14} />
                          {formatDate(team.created_at)}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Row - Member Details */}
                    {expandedTeam === team.id && team.members?.length > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan="7" className="px-4 py-4">
                          <div className="ml-4">
                            <h4 className="font-medium text-gray-700 mb-3">Team Members</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {team.members.map((member, idx) => (
                                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-3">
                                  <div className="font-medium text-gray-900">{member.name}</div>
                                  <div className="text-sm text-gray-500">{member.email}</div>
                                  {member.phone && <div className="text-sm text-gray-500">{member.phone}</div>}
                                  {member.college && (
                                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                                      <Building size={12} /> {member.college}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <Users className="mx-auto text-gray-300 mb-3" size={48} />
                    <div className="text-gray-500">No teams found</div>
                    <div className="text-sm text-gray-400">Try adjusting your filters</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * 25) + 1} - {Math.min(pagination.page * 25, pagination.total)} of {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                disabled={pagination.page === 1}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="px-4 py-2 text-sm text-gray-600">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamRegistryAdmin;
